import React, { useState, useEffect } from "react";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { createAnnouncement, UploadFeatureImageUrl, UploadBlogBodyImage, employeesApi } from "proxyUrl";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { BsFillImageFill } from "react-icons/bs";
import { useSelector } from "react-redux";

// Template data
const announcementTemplates = {
  birthday: [
    {
      id: "birthday-1",
      name: "Birthday Celebration - Warm Wishes",
      title: "🎉 Happy Birthday [name]! 🎉",
      description: `<p>We are thrilled to celebrate another wonderful year! Join us in wishing a very <strong>Happy Birthday</strong> to <strong>[name]</strong>!</p>
        <p>[name], your dedication, hard work, and positive energy make our workplace a better place. May this special day bring you joy, laughter, and all the happiness you deserve!</p>
        <p>Here's to another year of success, growth, and amazing achievements! 🎂🎈</p>
        <p><em>Best wishes from the entire team!</em></p>`,
      announcementType: "Event",
    },
    {
      id: "birthday-2",
      name: "Birthday Celebration - Team Appreciation",
      title: "🎂 Birthday Wishes for [name]! 🎂",
      description: `<p>Today is a special day as we celebrate the birthday of <strong>[name]</strong>!</p>
        <p>[name], your contributions to our team have been exceptional, and we are grateful to have you with us. Your passion, creativity, and commitment inspire us all.</p>
        <p>Let's make this day memorable! Wishing you a year filled with new opportunities, exciting challenges, and countless moments of joy.</p>
        <p>Happy Birthday, [name]! May all your dreams come true! 🌟</p>`,
      announcementType: "Event",
    },
    {
      id: "birthday-3",
      name: "Birthday Celebration - Milestone",
      title: "🎈 Celebrating [name]'s Special Birthday! 🎈",
      description: `<p>It's time to celebrate! We're marking another milestone birthday for <strong>[name]</strong> today!</p>
        <p>[name], your journey with us has been nothing short of amazing. Your dedication, professionalism, and positive attitude have made a significant impact on our team.</p>
        <p>As you celebrate another year, we want you to know how much you are appreciated. Here's to health, happiness, and continued success!</p>
        <p>Have a fantastic birthday celebration, [name]! 🎊🎉</p>`,
      announcementType: "Event",
    },
    {
      id: "birthday-4",
      name: "Birthday Celebration - Joyful",
      title: "🎁 Happy Birthday [name] - Spread the Joy! 🎁",
      description: `<p>Another year, another reason to celebrate! Today we're celebrating the birthday of <strong>[name]</strong>!</p>
        <p>[name], your enthusiasm and positive spirit brighten our workplace every day. We're lucky to have someone as wonderful as you on our team.</p>
        <p>May this birthday bring you endless joy, wonderful surprises, and moments that make you smile. Here's to celebrating you today and always!</p>
        <p>Wishing you a day as special as you are, [name]! 🎂✨</p>`,
      announcementType: "Event",
    },
  ],
  anniversary: [
    {
      id: "anniversary-1",
      name: "Work Anniversary - Milestone",
      title: "🎊 Celebrating [name]'s Work Anniversary! 🎊",
      description: `<p>Today marks a special milestone! We're celebrating the work anniversary of <strong>[name]</strong>!</p>
        <p>[name], your commitment, hard work, and loyalty have been instrumental in our success. Over the years, you've shown exceptional dedication and have become an invaluable part of our team.</p>
        <p>Thank you for your continued contributions and for being such an important part of our journey. Here's to many more years of collaboration and success!</p>
        <p>Congratulations on this achievement, [name]! 🎉</p>`,
      announcementType: "Event",
    },
    {
      id: "anniversary-2",
      name: "Work Anniversary - Appreciation",
      title: "🏆 Celebrating [name]'s Years of Excellence! 🏆",
      description: `<p>We're thrilled to celebrate another year of excellence! Today we honor the work anniversary of <strong>[name]</strong>!</p>
        <p>[name], your professionalism, expertise, and positive attitude have made a lasting impact on our organization. You've consistently gone above and beyond, and we're grateful for everything you do.</p>
        <p>Your journey with us has been filled with achievements, growth, and meaningful contributions. We look forward to many more successful years together!</p>
        <p>Thank you for being an amazing part of our team, [name]! 🌟</p>`,
      announcementType: "Event",
    },
    {
      id: "anniversary-3",
      name: "Work Anniversary - Gratitude",
      title: "💼 Thank You [name] for Your Dedication! 💼",
      description: `<p>Today we celebrate a significant milestone - the work anniversary of <strong>[name]</strong>!</p>
        <p>[name], your unwavering commitment, expertise, and collaborative spirit have been key to our team's success. You've not only met expectations but consistently exceeded them.</p>
        <p>As we reflect on the years we've worked together, we're reminded of the value you bring to our organization every single day. Your contributions don't go unnoticed.</p>
        <p>Here's to celebrating you and the many achievements ahead, [name]! 🎈</p>`,
      announcementType: "Event",
    },
    {
      id: "anniversary-4",
      name: "Work Anniversary - Recognition",
      title: "⭐ [name]'s Years of Service - We Appreciate You! ⭐",
      description: `<p>Join us in celebrating the work anniversary of <strong>[name]</strong>!</p>
        <p>[name], your dedication, hard work, and positive influence have shaped our team in countless ways. You've been a mentor, a collaborator, and a friend to many.</p>
        <p>This milestone represents not just time, but the impact you've made, the relationships you've built, and the excellence you've consistently delivered.</p>
        <p>Thank you for being an integral part of our success story, [name]! 🎊</p>`,
      announcementType: "Event",
    },
  ],
  other: [
    {
      id: "other-1",
      name: "Company Achievement",
      title: "🎯 Celebrating a Major Achievement! 🎯",
      description: `<p>We're excited to share some fantastic news! Our team has achieved a significant milestone that deserves recognition!</p>
        <p>This achievement reflects the hard work, dedication, and collaborative spirit of our entire team. Together, we've overcome challenges and reached new heights.</p>
        <p>Let's take a moment to celebrate this success and acknowledge everyone who contributed to making this possible. Your efforts have made a real difference!</p>
        <p>Here's to continued success and many more achievements ahead! 🚀</p>`,
      announcementType: "General",
    },
    {
      id: "other-2",
      name: "Team Recognition",
      title: "👏 Outstanding Team Performance! 👏",
      description: `<p>We're proud to recognize the exceptional performance of our team!</p>
        <p>Your collective efforts, innovative thinking, and commitment to excellence have set a new standard. This recognition is well-deserved and reflects the high-quality work you consistently deliver.</p>
        <p>Thank you for going above and beyond. Your dedication inspires us all and drives our continued success.</p>
        <p>Keep up the amazing work! 🌟</p>`,
      announcementType: "General",
    },
    {
      id: "other-3",
      name: "Holiday Greeting",
      title: "🎄 Season's Greetings! 🎄",
      description: `<p>As we approach this special time of year, we want to extend our warmest wishes to everyone!</p>
        <p>This has been a remarkable year, and we're grateful for each and every one of you. Your contributions have made this year a success, and we're excited about what's ahead.</p>
        <p>May this season bring you joy, peace, and time to relax and recharge with your loved ones. We hope you return refreshed and ready for new adventures!</p>
        <p>Wishing you all a wonderful holiday season! 🎁✨</p>`,
      announcementType: "General",
    },
    {
      id: "other-4",
      name: "Welcome New Team Member",
      title: "👋 Welcome [name] to the Team! 👋",
      description: `<p>We're thrilled to welcome <strong>[name]</strong> to our team!</p>
        <p>[name], we're excited to see the fresh perspectives, ideas, and energy you'll bring to our organization. Our team is stronger with you on board, and we look forward to working together.</p>
        <p>Please don't hesitate to reach out if you have any questions or need support. We're here to help you succeed and feel at home in our team.</p>
        <p>Welcome aboard, [name]! We're glad to have you with us! 🎉</p>`,
      announcementType: "General",
    },
  ],
};

const AnnouncementForm = () => {
  // Permissions
  const loggedUser = useSelector((state) => state.user.user || {});
  const userPermissions = loggedUser.userPermission || [];
  const canCreateAnnouncement = userPermissions.includes("create announcement");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [announcementType, setAnnouncementType] = useState("General");
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState(null);
  const [status, setStatus] = useState("pending");
  const [notification, setNotification] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [personName, setPersonName] = useState("");
  const [employees, setEmployees] = useState([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("");

  const navigate = useNavigate();

  // Fetch employees on component mount
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const { data } = await axios.get(employeesApi, { withCredentials: true });
        setEmployees(data?.data || data?.users || data || []);
      } catch (error) {
        console.error("employees fetch error", error);
        // Don't show error toast, just silently fail since it's optional
      }
    };
    fetchEmployees();
  }, []);

  // Helper function to get employee name
  const getEmployeeName = (employee) => {
    if (!employee) return "";
    return (
      `${employee.firstName || ""} ${employee.lastName || ""}`.trim() ||
      employee.name ||
      employee.fullName ||
      ""
    );
  };

  // Function to replace [name] placeholder with actual name
  const replaceNamePlaceholder = (text, name) => {
    if (!name) return text;
    return text.replace(/\[name\]/g, name);
  };

  const handleImage = (e) => {
    const selectedFile = e.target.files[0];
    setImage(selectedFile);
    if (selectedFile) {
      setPreview(URL.createObjectURL(selectedFile));
    }
  };

  const handleTemplateChange = (e) => {
    const templateId = e.target.value;
    setSelectedTemplate(templateId);

    if (templateId === "") {
      // Clear form if "None" is selected
      setTitle("");
      setDescription("");
      setAnnouncementType("General");
      setPersonName("");
      setSelectedEmployeeId("");
      return;
    }

    // Find the selected template
    let template = null;
    for (const category of Object.values(announcementTemplates)) {
      template = category.find((t) => t.id === templateId);
      if (template) break;
    }

    if (template) {
      // Auto-fill form with template content (replace [name] if name is provided)
      const finalTitle = replaceNamePlaceholder(template.title, personName);
      const finalDescription = replaceNamePlaceholder(template.description, personName);
      setTitle(finalTitle);
      setDescription(finalDescription);
      setAnnouncementType(template.announcementType);
      toast.success("Template applied successfully!", {
        position: "top-center",
        autoClose: 2000,
      });
    }
  };

  // Handle employee selection
  const handleEmployeeChange = (e) => {
    const employeeId = e.target.value;
    setSelectedEmployeeId(employeeId);

    if (employeeId === "") {
      // Clear employee selection, keep manual name if entered
      return;
    }

    // Find selected employee
    const selectedEmployee = employees.find((emp) => String(emp.id) === String(employeeId));
    if (selectedEmployee) {
      const employeeName = getEmployeeName(selectedEmployee);
      setPersonName(employeeName);

      // If a template is selected, update the title and description with the employee name
      if (selectedTemplate) {
        let template = null;
        for (const category of Object.values(announcementTemplates)) {
          template = category.find((t) => t.id === selectedTemplate);
          if (template) break;
        }

        if (template) {
          const finalTitle = replaceNamePlaceholder(template.title, employeeName);
          const finalDescription = replaceNamePlaceholder(template.description, employeeName);
          setTitle(finalTitle);
          setDescription(finalDescription);
        }
      }
    }
  };

  // Handle name change and update title/description if template is selected
  const handleNameChange = (e) => {
    const newName = e.target.value;
    setPersonName(newName);

    // Clear employee selection if user manually types a name
    if (newName && selectedEmployeeId) {
      setSelectedEmployeeId("");
    }

    // If a template is selected, update the title and description with the new name
    if (selectedTemplate) {
      let template = null;
      for (const category of Object.values(announcementTemplates)) {
        template = category.find((t) => t.id === selectedTemplate);
        if (template) break;
      }

      if (template) {
        const finalTitle = replaceNamePlaceholder(template.title, newName);
        const finalDescription = replaceNamePlaceholder(template.description, newName);
        setTitle(finalTitle);
        setDescription(finalDescription);
      }
    }
  };

  if (!canCreateAnnouncement) {
    return (
      <div className="flex h-64 items-center justify-center text-sm text-gray-600">
        You are not authorized to create announcements.
      </div>
    );
  }

  const sendData = async (e) => {
    e.preventDefault();

    if (!canCreateAnnouncement) {
      toast.error("You do not have permission to create announcements");
      return;
    }

    // Validate required fields
    if (!title || !description) {
      toast.error("Title and description are required!", {
        position: "top-center",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      return;
    }

    // Validate title length
    if (title.length > 120) {
      toast.error("Title must be 120 characters or less!", {
        position: "top-center",
        autoClose: 2000,
      });
      return;
    }

    setSubmitted(true);

    try {
      let imageUrl = null;

      // Upload image if provided
      if (image) {
        const formData = new FormData();
        formData.append("image", image);

        const uploadResponse = await axios.post(UploadFeatureImageUrl, formData, {
          withCredentials: true,
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        if (uploadResponse.status === 200) {
          imageUrl = uploadResponse.data.imageUrl;
        }
      }

      // Replace [name] in title and description before submitting (in case user edited manually)
      const finalTitle = replaceNamePlaceholder(title.trim(), personName);
      const finalDescription = replaceNamePlaceholder(description, personName);

      // Create announcement
      const announcementData = {
        title: finalTitle,
        description: finalDescription,
        announcementType: announcementType,
        image: imageUrl,
        status: status,
        notification: parseInt(notification),
      };

      const response = await axios.post(createAnnouncement, announcementData, {
        withCredentials: true,
      });

      if (response.status === 201) {
        toast.success("Announcement created successfully!", {
          position: "top-center",
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });

        const delayRefresh = () => {
          navigate("/admin/announcements");
        };
        setTimeout(delayRefresh, 2100);
      }
    } catch (error) {
      console.error("Error creating announcement:", error);
      toast.error(
        error.response?.data?.error || "Failed to create announcement!",
        {
          position: "top-center",
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        }
      );
      setSubmitted(false);
    }
  };

  return (
    <>
      <div className="mt-7 mb-2 rounded-lg bg-white shadow-xl dark:bg-navy-800 p-4">
        <div className="mx-auto w-[100%]">
          <form action="" method="POST" className="">
            <div className="">
              {/* Template Selector */}
              <div className="mb-6 rounded-lg border-2 border-indigo-600/20 bg-indigo-600/5 p-4">
                <label htmlFor="template" className="block text-sm font-semibold text-gray-700 mb-2">
                  Choose a Template (Optional)
                </label>
                <select
                  id="template"
                  value={selectedTemplate}
                  onChange={handleTemplateChange}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-600 focus:outline-none focus:ring-1 focus:ring-primaryBlue"
                >
                  <option value="">None - Start from scratch</option>
                  <optgroup label="🎂 Birthday Templates">
                    {announcementTemplates.birthday.map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.name}
                      </option>
                    ))}
                  </optgroup>
                  <optgroup label="🎊 Anniversary Templates">
                    {announcementTemplates.anniversary.map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.name}
                      </option>
                    ))}
                  </optgroup>
                  <optgroup label="📢 Other Event Templates">
                    {announcementTemplates.other.map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.name}
                      </option>
                    ))}
                  </optgroup>
                </select>
                <p className="mt-2 text-xs text-gray-500">
                  Select a template to auto-fill the form with pre-written content. You can still edit the content after applying a template.
                </p>
              </div>

              {/* Employee Selector and Name Field - Show only for birthday, anniversary, or new joining templates */}
              {(selectedTemplate.startsWith("birthday-") || 
                selectedTemplate.startsWith("anniversary-") || 
                selectedTemplate === "other-4") && (
                <div className="mb-4 space-y-4">
                  {/* Employee Selector */}
                  <div className="input relative w-full">
                    <label htmlFor="selectedEmployee">Select Employee (Optional)</label>
                    <select
                      id="selectedEmployee"
                      value={selectedEmployeeId}
                      onChange={handleEmployeeChange}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-600 focus:outline-none focus:ring-1 focus:ring-primaryBlue"
                    >
                      <option value="">None - Enter name manually</option>
                      {employees.map((emp) => {
                        const empName = getEmployeeName(emp);
                        return (
                          <option key={emp.id} value={emp.id}>
                            {empName || `Employee #${emp.id}`}
                          </option>
                        );
                      })}
                    </select>
                    <p className="mt-1 text-xs text-gray-500">
                      Select an employee to automatically fill the name, or enter manually below.
                    </p>
                  </div>

                  {/* Manual Name Field */}
                  <div className="input relative w-full">
                    <label htmlFor="personName">Person's Name {selectedEmployeeId ? "(Auto-filled from employee)" : ""}</label>
                    <input
                      type="text"
                      id="personName"
                      placeholder="Enter the person's name"
                      name="personName"
                      value={personName}
                      onChange={handleNameChange}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-600 focus:outline-none focus:ring-1 focus:ring-primaryBlue"
                      disabled={!!selectedEmployeeId}
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      The name will automatically replace [name] in the title and description.
                      {selectedEmployeeId && " Clear employee selection above to edit manually."}
                    </p>
                  </div>
                </div>
              )}

              {/* Main Form Layout: Left (Fields) and Right (Image) */}
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 mb-6">
                {/* Left Side - Form Fields */}
                <div className="space-y-4">
                  <div className="input relative w-full">
                    <label htmlFor="title">Title *</label>
                    <input
                      type="text"
                      placeholder="Enter announcement title"
                      maxLength={120}
                      name="title"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      required
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-600 focus:outline-none focus:ring-1 focus:ring-primaryBlue"
                    />
                    <span className="text-xs text-gray-500">
                      {title.length}/120 characters
                    </span>
                  </div>

                  <div className="input relative w-full">
                    <label htmlFor="announcementType">Announcement Type *</label>
                    <select
                      id="announcementType"
                      value={announcementType}
                      onChange={(e) => setAnnouncementType(e.target.value)}
                      required
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-600 focus:outline-none focus:ring-1 focus:ring-primaryBlue"
                    >
                      <option value="General">General</option>
                      <option value="Event">Event</option>
                    </select>
                  </div>

                  <div className="input relative w-full">
                    <label htmlFor="status">Status</label>
                    <select
                      id="status"
                      value={status}
                      onChange={(e) => setStatus(e.target.value)}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-600 focus:outline-none focus:ring-1 focus:ring-primaryBlue"
                    >
                      <option value="draft">Draft</option>
                      <option value="pending">Pending</option>
                      {/* <option value="approved">Approved</option> */}
                    </select>
                  </div>

                  <div className="input relative w-full">
                    <label htmlFor="notification">Notification Target</label>
                    <select
                      id="notification"
                      value={notification}
                      onChange={(e) => setNotification(e.target.value)}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-600 focus:outline-none focus:ring-1 focus:ring-primaryBlue"
                    >
                      <option value={0}>All</option>
                      <option value={1}>VL</option>
                      <option value={2}>Vnaya</option>
                      <option value={3}>Creed</option>
                      <option value={4}>ASI</option>
                    </select>
                  </div>
                </div>

                {/* Right Side - Image Preview and Upload */}
                <div className="space-y-4">
                  <div className="input relative w-full">
                    <label htmlFor="image">Upload Image</label>
                    <input
                      type="file"
                      name="image"
                      accept="image/*"
                      onChange={handleImage}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-600 focus:outline-none focus:ring-1 focus:ring-primaryBlue"
                    />
                  </div>
                  
                  <div className="input relative w-full">
                    <label>Image Preview</label>
                    <div className="mt-2 flex items-center justify-center rounded-lg border-2 border-gray-200 bg-gray-50 p-4 min-h-[250px]">
                      {preview ? (
                        <img
                          className="max-h-[250px] w-full rounded-md object-contain"
                          src={preview}
                          alt="Preview"
                        />
                      ) : (
                        <div className="flex flex-col items-center justify-center text-gray-400">
                          <BsFillImageFill className="text-6xl mb-2" />
                          <p className="text-sm">No image selected</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="relative mx-2">
                <h2 className="mb-1">Description *</h2>
                <CKEditor
                  editor={ClassicEditor}
                  data={description}
                  onReady={(editor) => {
                    // Editor is ready to use
                  }}
                  onChange={(event, editor) => {
                    const data = editor.getData();
                    setDescription(data);
                  }}
                  config={{
                    ckfinder: {
                      uploadUrl: `${UploadBlogBodyImage}`,
                      withCredentials: true,
                    },
                  }}
                />
              </div>

              {submitted ? (
                <div className="field cursor-pointer">
                  <div className="my-5 rounded-md bg-gray-300 py-3 px-12 text-white">
                    <button disabled>Loading....</button>
                  </div>
                </div>
              ) : (
                <div className="field cursor-pointer" onClick={sendData}>
                  <div className="my-5 rounded-md bg-indigo-600 py-3 px-12 text-white">
                    <button type="submit">Submit</button>
                  </div>
                </div>
              )}
            </div>
          </form>
        </div>
      </div>
      <ToastContainer />
    </>
  );
};

export default AnnouncementForm;

